import { ExpressPriceType } from "../../types/express-document.type";
import { roundHalfUp } from "../../utils/money";

/**
 * Live PREVIEW of an Express document's totals while it is keyed in — the rule the server
 * applies when it stores the document, rounded the same way (half away from zero, 2 dp). The
 * server stays authoritative: a saved document shows the totals it stored, never these.
 *
 *   subtotal       = Σ line amount
 *   after_discount = subtotal − discount
 *   0 ไม่มี VAT:  vat = 0;                               base = after_discount; net = after_discount
 *   1 รวม VAT:   vat = after_discount × r / (100 + r);  base = after_discount − vat; net = after_discount
 *   2 แยก VAT:   base = after_discount; vat = base × r / 100;                       net = base + vat
 */

export interface ExpressTotals {
  /** จำนวนเงิน — Σ line amounts. */
  subtotal: number;
  /** หักส่วนลด */
  discountAmount: number;
  /** มูลค่า — the amount VAT is worked on. */
  baseAmount: number;
  /** ภาษี */
  vatAmount: number;
  /** จำนวนเงินรวมทั้งสิ้น */
  netAmount: number;
}

export interface ExpressPaymentTotals {
  /** ชำระโดยอื่นๆ — Σ payment lines. */
  otherPaymentAmount: number;
  /** ยอดชำระจริง — cash + other + ภาษี ณ ที่จ่าย. */
  receivedAmount: number;
}

/** An R5 / P4 line's amount; a negative unit price offsets the other lines. */
export const lineAmount = (qty: number, unitPrice: number): number =>
  roundHalfUp(qty * unitPrice);

/** A sum to the satang, free of the float noise adding many 2-dp figures leaves behind. */
export const sumAmounts = (amounts: number[]): number =>
  roundHalfUp(amounts.reduce((sum, amount) => sum + amount, 0));

export function computeExpressTotals(
  lineAmounts: number[],
  discountAmount: number,
  priceType: ExpressPriceType,
  vatRate: number,
): ExpressTotals {
  const subtotal = sumAmounts(lineAmounts);
  const afterDiscount = roundHalfUp(subtotal - discountAmount);

  let baseAmount = afterDiscount;
  let vatAmount = 0;
  let netAmount = afterDiscount;
  if (priceType === 1) {
    vatAmount = roundHalfUp((afterDiscount * vatRate) / (100 + vatRate));
    baseAmount = roundHalfUp(afterDiscount - vatAmount);
  } else if (priceType === 2) {
    vatAmount = roundHalfUp((baseAmount * vatRate) / 100);
    netAmount = roundHalfUp(baseAmount + vatAmount);
  }

  return { subtotal, discountAmount, baseAmount, vatAmount, netAmount };
}

export const computePaymentTotals = (
  cashAmount: number,
  whtAmount: number,
  paymentLineAmounts: number[],
): ExpressPaymentTotals => {
  const otherPaymentAmount = sumAmounts(paymentLineAmounts);
  return {
    otherPaymentAmount,
    receivedAmount: roundHalfUp(cashAmount + otherPaymentAmount + whtAmount),
  };
};
